import React, { useContext, useState } from "react";
import { useAssignment } from "../../../context/AssignmentContext";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";
import { message } from "antd";
import { toast } from "react-toastify";
import {
  CallVideoIcon,
  LinkIcon,
  MicIcon,
  OcrOnIcon,
  PaperclipIcon,
} from "@fluentui/react-northstar";

const IntelligentGrading = React.createContext();

export const useIntelligentGrading = () => {
  return useContext(IntelligentGrading);
};

const url = process.env.REACT_APP_EP_URL;

const formatAttachmentData = async (attachments) => {
  try {
    const formattedAttachments = attachments.map((attachment) => {
      return {
        id: attachment.id,
        type: attachment.type,
        info: ["link", "file"].includes(attachment.type)
          ? attachment.url.split(",")
          : attachment.url,
        icon:
          attachment.type === "text" ? (
            <OcrOnIcon />
          ) : attachment.type === "voice" ? (
            <MicIcon />
          ) : attachment.type === "video" ? (
            <CallVideoIcon />
          ) : attachment.type === "link" ? (
            <LinkIcon />
          ) : attachment.type === "file" ? (
            <PaperclipIcon />
          ) : null,
        content:
          attachment.type === "text"
            ? "Text Response"
            : attachment.type === "voice"
              ? "Audio Response"
              : attachment.type === "video"
                ? "Video Response"
                : attachment.type === "link"
                  ? "Link Response"
                  : attachment.type === "file"
                    ? "File Response"
                    : null,
        extractedText: attachment.extractedText || "",
      };
    });

    return formattedAttachments;
  } catch (error) {
    console.log(error);
  } finally {
  }
};

export const IntelligentGradingContext = ({ children, user }) => {
  const { currentAssignmentData } = useAssignment();

  const [isLoading, setIsLoading] = useState(false);
  const [allAssignmentSubmissions, setAllAssignmentSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedSubmissionIndex, setSelectedSubmissionIndex] = useState(0);
  const [rubrics, setRubrics] = useState([]);
  const [overallScore, setOverallScore] = useState({ score: 0, max: 0 });
  const [students, setStudents] = useState([]); // Batch grading
  const [activeTab, setActiveTab] = useState("single");
  const [isStudentSubmissionLoading, setIsStudentSubmissionLoading] =
    useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);
  // Batch grading
  const [statusWiseCounts, setStatusWiseCounts] = useState({
    approved: 0,
    gradesPending: 0,
    pending: 0,
    resubmit: 0,
    total: 0,
  });

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFilterParameters, setSelectedFilterParameters] = useState({
    grades: [],
    statuses: [],
    confidence: 0,
  });
  const [originalStudents, setOriginalStudents] = useState([]);

  // Annotations
  const [annotations, setAnnotations] = useState([]);
  const [textAnnotations, setTextAnnotations] = useState([]);
  const [voiceAnnotations, setVoiceAnnotations] = useState([]);
  const [videoAnnotations, setVideoAnnotations] = useState([]);
  const [linkAnnotations, setLinkAnnotations] = useState([]);
  const [fileAnnotations, setFileAnnotations] = useState([]);

  const getMaxScore = (rubrics, field) => {
    return rubrics.reduce((acc, curr) => {
      if (curr[field] > acc) {
        acc = curr[field];
      }
      return acc;
    }, 0);
  };

  const findRubricItem = (criteria, criteriaId) => {
    return criteria.find((item) => item.criteriaId === criteriaId);
  };

  const getColorBasedOnPoints = (confidence) => {
    if (confidence >= 95) return "#389e0d"; // Dark green for exceptional confidence
    if (confidence >= 90) return "#52c41a"; // Green for excellent confidence
    if (confidence >= 85) return "#1890ff"; // Blue for very good confidence
    if (confidence >= 80) return "#40a9ff"; // Light blue for good confidence
    if (confidence >= 75) return "#faad14"; // Orange for moderate confidence
    if (confidence >= 70) return "#fa8c16"; // Dark orange for borderline confidence
    if (confidence >= 60) return "#ff4d4f"; // Light red for concerning confidence
    return "#f5222d"; // Red for low confidence
  };

  const formatRubricScores = (assignmentRubrics, selectedRubric) => {
    return selectedRubric.map((rubric) => {
      const selectedRubricItem = assignmentRubrics.find(
        (item) => item.rubricItemId === rubric.rubricItemId,
      );
      const points =
        findRubricItem(selectedRubricItem.rubricCriteria, rubric.criteriaId)
          ?.points || 0;
      const max = getMaxScore(selectedRubricItem.rubricCriteria, "points");
      return {
        id: rubric.rubricItemId,
        criteriaId: rubric.criteriaId,
        label: selectedRubricItem.rubricItemTitle,
        score: points,
        options: selectedRubricItem.rubricCriteria,
        max: max,
        comment: rubric.justification,
        confidence: rubric.confidence,
        color: getColorBasedOnPoints((points / max) * 100),
      };
    });
  };

  const getOverallScore = (rubrics) => {
    return rubrics.reduce(
      (acc, curr) => {
        acc.score += curr.score;
        acc.max += curr.max;
        return acc;
      },
      { score: 0, max: 0 },
    );
  };

  function getGrade(percentage) {
    if (percentage < 0 || percentage > 100) {
      return "Invalid percentage";
    }

    if (percentage > 90) {
      return "A+";
    } else if (percentage > 85) {
      return "A";
    } else if (percentage > 80) {
      return "A-";
    } else if (percentage > 70) {
      return "B+";
    } else if (percentage > 60) {
      return "B";
    } else {
      return "F";
    }
  }

  const formateStudentsForBatchGrading = async (
    assignmentRubrics,
    submissions,
  ) => {
    return await Promise.all(
      submissions.map(async (submission) => {
        let rubrics = [];
        let overallScore;
        if (
          submission?.submissionStatus === "Grades Pending" &&
          submission.aiGradingStatus === "Completed"
        ) {
          rubrics = formatRubricScores(
            assignmentRubrics,
            submission.aiGradingResult.results,
          );
          overallScore = getOverallScore(rubrics);
        } else if (
          submission?.submissionStatus === "Completed" &&
          submission.aiGradingStatus === "Completed"
        ) {
          rubrics = formatRubricScores(
            assignmentRubrics,
            submission.teacherGradingResult.results,
          );
          overallScore = getOverallScore(rubrics);
        }

        return {
          id: submission.id,
          name: submission?.StudentDatum?.StudentName,
          email: submission.studentId,
          image: submission?.StudentDatum?.studPhoto?.Foto
            ? submission?.StudentDatum?.studPhoto?.Foto +
              process.env.REACT_APP_AZURE_SAS_TOKEN.replace(/"/g, "")
            : "",
          submissionDate: submission.submissionDate,
          grade:
            (submission?.submissionStatus === "Completed" ||
              submission?.submissionStatus === "Grades Pending") &&
            submission.aiGradingStatus === "Completed"
              ? getGrade((overallScore.score / overallScore.max) * 100)
              : null,
          confidence: submission?.aiGradingResult?.overallConfidence || 0,
          timeTaken: submission?.aiGradingResult?.timeTaken || 0,
          rubrics,
          submissionStatus: submission.submissionStatus,
          aiGradingStatus: submission.aiGradingStatus,
          isSelected: false,
          overWrite: submission?.teacherGradingResult?.overWrite || false,
        };
      }),
    );
  };

  // get all the submissions of the assignment
  const getAllAssignmentSubmissions = async (assignment) => {
    try {
      setIsLoading(true);
      let response = await axios({
        method: "get",
        url: `${url}/api/assignment/get-assignment-students${user.slug}&id=${assignment.id}`,
        headers: {
          Authorization: "Bearer " + user.accessToken,
        },
      });

      setStatusWiseCounts(response.data.statusWiseCounts);
      setAllAssignmentSubmissions(response.data.results);
      const firstSubmission = response.data.results[0];

      if (
        firstSubmission.submissionStatus === "Grades Pending" &&
        firstSubmission.aiGradingStatus === "Completed"
      ) {
        // format the rubrics for intelligent grading feature
        const rubrics = formatRubricScores(
          assignment.rubric.rubricItems,
          firstSubmission.aiGradingResult.results,
        );
        setRubrics(rubrics);
        // get the overall score
        const overallScore = getOverallScore(rubrics);
        setOverallScore(overallScore);
        setAnnotations(firstSubmission.aiGradingResult.annotations);
      } else if (
        firstSubmission.submissionStatus === "Completed" &&
        firstSubmission.aiGradingStatus === "Completed"
      ) {
        const rubrics = formatRubricScores(
          assignment.rubric.rubricItems,
          firstSubmission.teacherGradingResult.results,
        );
        setRubrics(rubrics);
        const overallScore = getOverallScore(rubrics);
        setOverallScore(overallScore);
        setAnnotations(firstSubmission.teacherGradingResult.annotations);
      }

      setSelectedSubmission(firstSubmission); // set the first submission as the selected submission
      setSelectedSubmissionIndex(0); // set the selected submission index to 0
      const students = await formateStudentsForBatchGrading(
        assignment.rubric.rubricItems,
        response.data.results,
      );

      setStudents(students);
      setOriginalStudents(students); // Store original students for filtering
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const formatRubricsForSubmission = (givenRubrics, assignmentRubrics) => {
    return assignmentRubrics.map((item) => {
      const givenRubricItem = givenRubrics.find(
        (rubric) => rubric.rubricItemId === item.rubricItemId,
      );
      const criteria = item.rubricCriteria.find(
        (criteria) => criteria.criteriaId === givenRubricItem.criteriaId,
      );
      return {
        rubricItemId: item.rubricItemId,
        rubricItemTitle: item.rubricItemTitle,
        criteriaId: givenRubricItem.criteriaId,
        criteriaTitle: criteria.criteriaTitle,
        grades: givenRubricItem.points,
        maxGrades: getMaxScore(item.rubricCriteria, "points"),
        confidence: givenRubricItem.confidence,
        justification: givenRubricItem.justification,
      };
    });
  };

  const submitGrading = async (status, submission, resubmitReason, type) => {
    try {
      const teacherGradingResult =
        type === "single-approve"
          ? { ...submission.aiGradingResult, approvedAiGrading: true }
          : {
              ...submission.teacherGradingResult,
              results: submission.teacherGradingResult.results.map((item) => {
                return {
                  confidence: item.confidence,
                  criteriaId: item.criteriaId,
                  rubricItemId: item.id,
                  justification: item.justification,
                };
              }),
            };
      const payload = {
        id: submission.id,
        assingmentId: submission.assingmentId,
        submissionStatus: status,
        gradedOn: status === "Completed" ? moment().toISOString() : null,
        teacherGradingResult,
        gradingsData:
          status !== "Resubmit"
            ? {
                rubricId: currentAssignmentData.rubric.rubricId,
                totalPoint: currentAssignmentData.markingType,
                givenPoint: submission.overallScore.score,
                gradings: formatRubricsForSubmission(
                  teacherGradingResult.results,
                  currentAssignmentData.rubric.rubricItems,
                ),
              }
            : null,
      };
      setIsLoading(true);
      await axios({
        method: "post",
        url: `${url}/api/assignment/teacher-submit-assignment${user.slug}`,
        data: payload,
        headers: {
          Authorization: "Bearer " + user.accessToken,
        },
      });
      // update the status wise counts
      setStatusWiseCounts((prev) => {
        return {
          ...prev,
          gradesPending: prev.gradesPending - 1,
          approved: prev.approved + 1,
        };
      });

      let clonnedSubmissions = [...allAssignmentSubmissions];
      const submissionIndex = clonnedSubmissions.findIndex(
        (sub) => sub.id === submission.id,
      );
      clonnedSubmissions[submissionIndex].submissionStatus = status;
      clonnedSubmissions[submissionIndex].teacherGradingResult =
        payload.teacherGradingResult;
      setAllAssignmentSubmissions(clonnedSubmissions);

      // Update the students array for reflect changes on batch grading page

      let clonedStudents = [...students];
      const studentIndex = clonedStudents.findIndex(
        (student) => student.id === submission.id,
      );
      clonedStudents[studentIndex].submissionStatus = status;

      clonedStudents[studentIndex].rubrics = formatRubricScores(
        currentAssignmentData.rubric.rubricItems,
        submission.teacherGradingResult.results,
      );
      setStudents(clonedStudents);

      setSelectedSubmission({
        ...selectedSubmission,
        submissionStatus: status,
        teacherGradingResult: payload.teacherGradingResult,
      });
      setRubrics(
        formatRubricScores(
          currentAssignmentData.rubric.rubricItems,
          payload.teacherGradingResult.results,
        ),
      );
      toast.success("Grading submitted successfully!");
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const handleSubmissionChange = async (index) => {
    let selectedSubmission = allAssignmentSubmissions[index];
    // Switch to single assignment view
    setActiveTab("single");
    console.log(
      "selectedSubmission:handleSubmissionChange",
      allAssignmentSubmissions,
    );

    // set the selected submission and submission index for show only student info
    setSelectedSubmission(selectedSubmission);
    setSelectedSubmissionIndex(index);

    const isSubmissionDoneByStudent =
      selectedSubmission.submissionStatus === "Grades Pending" &&
      selectedSubmission.aiGradingStatus === "Completed";

    // if the submission is done by the student, format the rubrics and set the overall score
    if (isSubmissionDoneByStudent) {
      setIsStudentSubmissionLoading(true);
      const studentSubmission = await getStudentsSubmission(
        selectedSubmission.id,
        selectedSubmission.studentId,
      );
      selectedSubmission = {
        ...selectedSubmission,
        attachments: studentSubmission.attachments,
      };

      // format the rubrics for intelligent grading feature
      const rubrics = formatRubricScores(
        currentAssignmentData.rubric.rubricItems,
        selectedSubmission.aiGradingResult.results,
      );
      setRubrics(rubrics);
      // get the overall score
      const overallScore = getOverallScore(rubrics);
      setOverallScore(overallScore);
      setAnnotations(selectedSubmission.aiGradingResult.annotations);
    } else if (
      selectedSubmission.submissionStatus === "Completed" &&
      selectedSubmission.aiGradingStatus === "Completed"
    ) {
      setIsStudentSubmissionLoading(true);
      const studentSubmission = await getStudentsSubmission(
        selectedSubmission.id,
        selectedSubmission.studentId,
      );
      selectedSubmission = {
        ...selectedSubmission,
        attachments: studentSubmission.attachments,
      };
      const rubrics = formatRubricScores(
        currentAssignmentData.rubric.rubricItems,
        selectedSubmission.teacherGradingResult.results,
      );
      setRubrics(rubrics);
      // get the overall score
      const overallScore = getOverallScore(rubrics);
      setOverallScore(overallScore);
      setAnnotations(selectedSubmission.teacherGradingResult.annotations);
    }
    // set the selected submission and submission index
    setSelectedSubmission(selectedSubmission);
    setSelectedSubmissionIndex(index);
    setIsStudentSubmissionLoading(false);
  };

  const handleBulkApprove = async (submissionIds) => {
    try {
      setIsLoading(true);
      const payload = {
        submissions: submissionIds.map((id) => {
          const submission = allAssignmentSubmissions.find(
            (sub) => sub.id === id,
          );
          return {
            id,
            assignmentId: submission.assingmentId,
            submissionStatus: "Completed",
            gradedOn: moment().toISOString(),
            givenGrade: getOverallScore(
              formatRubricScores(
                currentAssignmentData.rubric.rubricItems,
                submission.aiGradingResult.results,
              ),
            ).score,
            teacherGradingResult: {
              ...submission.aiGradingResult,
              approvedAiGrading: true,
            },
            grades: formatRubricsForSubmission(
              submission.aiGradingResult.results,
              currentAssignmentData.rubric.rubricItems,
            ),
          };
        }),
      };

      const response = await axios({
        method: "post",
        url: `${url}/api/assignment/approve-bulk-assignment-submissions${user.slug}`,
        data: payload,
        headers: {
          Authorization: "Bearer " + user.accessToken,
        },
      });

      // Refresh assignment submissions after bulk approve
      await getAllAssignmentSubmissions(currentAssignmentData);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const handleBulkReject = async (submissionIds) => {
    try {
      setIsLoading(true);
      const payload = {
        assignmentId: currentAssignmentData.id,
        submissionIds,
        studentIds: submissionIds.map((id) => {
          const submission = allAssignmentSubmissions.find(
            (sub) => sub.id === id,
          );
          return submission.studentId;
        }),
      };

      await axios({
        method: "post",
        url: `${url}/api/assignment/reject-bulk-assignment-submissions${user.slug}`,
        data: payload,
        headers: {
          Authorization: "Bearer " + user.accessToken,
        },
      });

      // Refresh assignment submissions after bulk reject
      await getAllAssignmentSubmissions(currentAssignmentData);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const handleBulkRegenerate = async (payload) => {
    try {
      setIsLoading(true);

      await axios({
        method: "post",
        url: `${url}/api/assignment/regenerate-bulk-ai-grading${user.slug}`,
        data: payload,
        headers: {
          Authorization: "Bearer " + user.accessToken,
        },
      });

      // Refresh assignment submissions after bulk regenerate
      await getAllAssignmentSubmissions(currentAssignmentData);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const handleRegenerate = async (submissionId, studentId) => {
    try {
      setIsLoading(true);
      const payload = {
        submissionId,
        studentId,
        assignmentId: currentAssignmentData.id,
      };

      await axios({
        method: "post",
        url: `${url}/api/assignment/regenerate-ai-grading${user.slug}`,
        data: payload,
        headers: {
          Authorization: "Bearer " + user.accessToken,
        },
      });

      // Refresh assignment submissions after regenerating
      await getAllAssignmentSubmissions(currentAssignmentData);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  const formatSubmissionDate = (date) => {
    if (!date) return "";
    return moment(date).format("DD-MM-YYYY");
  };

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      message.loading({ content: "Preparing export...", key: "export" });

      // Transform the data into the required format
      const exportData = [];

      // Add headers row with styling
      const headers = [
        "Student Name",
        "Email",
        "Grade",
        "Submission Date",
        "Rubric Label",
        "Score",
        "Max",
        "Comment",
        "Confidence",
        "OverallScore",
        "Overall Max Score",
        "Status",
      ];

      allAssignmentSubmissions.forEach((submission) => {
        const student = students.find((s) => s.id === submission.id);
        const rubricRows = student?.rubrics || [];
        const overallScore = getOverallScore(rubricRows);

        // Add main student row
        const mainRow = {
          "Student Name": student?.name || "",
          Email: student?.email || "",
          Grade: student?.grade || "",
          "Submission Date": formatSubmissionDate(student?.submissionDate),
          "Rubric Label": rubricRows[0]?.label || "",
          Score: rubricRows[0]?.score || "",
          Max: rubricRows[0]?.max || "",
          Comment: rubricRows[0]?.comment || "",
          Confidence: rubricRows[0]?.confidence
            ? `${rubricRows[0].confidence}%`
            : "",
          OverallScore: overallScore?.score || "",
          "Overall Max Score": overallScore?.max || "",
          Status: submission?.submissionStatus || "",
        };
        exportData.push(mainRow);

        // Add additional rubric rows
        rubricRows.slice(1).forEach((rubric) => {
          exportData.push({
            "Student Name": "",
            Email: "",
            Grade: "",
            "Submission Date": "",
            "Rubric Label": rubric?.label || "",
            Score: rubric?.score || "",
            Max: rubric?.max || "",
            Comment: rubric?.comment || "",
            Confidence: rubric?.confidence ? `${rubric.confidence}%` : "",
            OverallScore: "",
            "Overall Max Score": "",
            Status: "",
          });
        });

        // Add empty row between students for better readability
        exportData.push({
          "Student Name": "",
          Email: "",
          Grade: "",
          "Submission Date": "",
          "Rubric Label": "",
          Score: "",
          Max: "",
          Comment: "",
          Confidence: "",
          OverallScore: "",
          "Overall Max Score": "",
          Status: "",
        });
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData, {
        header: headers,
      });

      // Set column widths
      const colWidths = {
        A: 20, // Student Name
        B: 25, // Email
        C: 8, // Grade
        D: 15, // Submission Date
        E: 20, // Rubric Label
        F: 8, // Score
        G: 8, // Max
        H: 30, // Comment
        I: 12, // Confidence
        J: 12, // OverallScore
        K: 12, // Overall Max Score
        L: 12, // Status
      };

      ws["!cols"] = Object.keys(colWidths).map((key) => ({
        wch: colWidths[key],
      }));

      // Add the worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, "Submissions");

      // Write file and trigger download
      XLSX.writeFile(
        wb,
        `${currentAssignmentData.assignmentTitle}_submissions.xlsx`,
      );

      message.success({
        content: "Export completed successfully!",
        key: "export",
        duration: 3,
      });
    } catch (error) {
      console.error("Export failed:", error);
      message.error({
        content: "Failed to export data. Please try again.",
        key: "export",
        duration: 3,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const getStudentsSubmission = async (submissionId, studentId) => {
    try {
      let response = await axios({
        method: "get",
        url: `${url}/api/assignment/get-assignment-submission${user.slug}&id=${submissionId}&aId=${currentAssignmentData.id}&studentId=${studentId}`,
        headers: {
          Authorization: "Bearer " + user.accessToken,
        },
      });
      if (response.status === 200) {
        let responseData = response.data?.results;
        if (responseData) {
          let formattedData = { ...responseData };
          if (responseData.attachmentData?.length) {
            let responseAttachments = responseData.attachmentData?.filter(
              (item) => item.aId === currentAssignmentData.id,
            );
            formattedData.attachments =
              await formatAttachmentData(responseAttachments);
          }

          return formattedData;
        }
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  // Add filter related functions
  const handleFilterOpen = () => {
    setIsFilterOpen(true);
  };

  const handleFilterClose = () => {
    setIsFilterOpen(false);
  };

  const onResetFilter = () => {
    setSelectedFilterParameters({
      grades: [],
      statuses: [],
      confidence: 0,
    });
    setStudents(originalStudents);
  };

  const onApplyFilter = () => {
    if (
      selectedFilterParameters.grades.length === 0 &&
      selectedFilterParameters.statuses.length === 0 &&
      selectedFilterParameters.confidence === 0
    ) {
      setStudents(originalStudents);
      return;
    }

    const filteredStudents = originalStudents.filter((student) => {
      const gradeMatch =
        selectedFilterParameters.grades.length === 0 ||
        selectedFilterParameters.grades.includes(student.grade);

      const statusMatch =
        selectedFilterParameters.statuses.length === 0 ||
        selectedFilterParameters.statuses.includes(student.submissionStatus);

      const confidenceMatch =
        selectedFilterParameters.confidence === 0 ||
        (student.confidence &&
          student.confidence >= selectedFilterParameters.confidence);

      return gradeMatch && statusMatch && confidenceMatch;
    });

    setStudents(filteredStudents);
  };

  const handleSendGradingReport = async (
    assignmentName,
    studentMail,
    studentName,
    reportFile,
  ) => {
    try {
      setIsSendingReport(true);
      const info = {
        assignmentName,
        studentMail,
        studentName,
      };

      const formData = new FormData();
      formData.append("info", JSON.stringify(info));
      formData.append("reports", reportFile, "AI Grading Report.pdf");

      const response = await axios({
        method: "post",
        url: `${url}/api/assignment/send-grading-report-to-student${user.slug}`,
        data: formData,
        headers: {
          Authorization: "Bearer " + user.accessToken,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200) {
        toast.success("Grading report sent successfully!");
      }
    } catch (error) {
      toast.error("Failed to send grading report!");
    } finally {
      setIsSendingReport(false);
    }
  };

  return (
    <IntelligentGrading.Provider
      value={{
        isLoading,
        setIsLoading,
        getAllAssignmentSubmissions,
        selectedSubmission,
        setSelectedSubmission,
        allAssignmentSubmissions,
        rubrics,
        selectedSubmissionIndex,
        handleSubmissionChange,
        overallScore,
        setOverallScore,
        statusWiseCounts,
        submitGrading,
        students,
        setStudents,
        handleBulkApprove,
        handleBulkReject,
        activeTab,
        setActiveTab,
        handleBulkRegenerate,
        handleRegenerate,
        handleExportToExcel,
        getGrade,
        isStudentSubmissionLoading,
        getColorBasedOnPoints,
        isExporting,
        getOverallScore,
        isFilterOpen,
        handleFilterOpen,
        handleFilterClose,
        selectedFilterParameters,
        setSelectedFilterParameters,
        onResetFilter,
        onApplyFilter,
        originalStudents,
        user,
        textAnnotations,
        setTextAnnotations,
        voiceAnnotations,
        setVoiceAnnotations,
        videoAnnotations,
        setVideoAnnotations,
        linkAnnotations,
        setLinkAnnotations,
        fileAnnotations,
        setFileAnnotations,
        annotations,
        setAnnotations,
        formatRubricScores,
        getStudentsSubmission,
        isSendingReport,
        setIsSendingReport,
        handleSendGradingReport,
      }}
    >
      {children}
    </IntelligentGrading.Provider>
  );
};
